import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";

import { Card } from "src/base-components/Card";
import { AddHighlightPopover } from "src/manualReviewNode/AddHighlightPopover";
import { HighlightRow } from "src/manualReviewNode/HighlightRow";
import { ManualReviewForm } from "src/manualReviewNode/ManualReviewNodeEditor";
import { HighlightsInner } from "src/manualReviewNode/types";

type Highlight = ManualReviewForm["highlights"][number] & { id: string };
type Highlights = Highlight[];

export const HighlightsEditor: React.FC<{
  immutable: boolean;
  fields: Highlights;
  append: (value: ManualReviewForm["highlights"][number]) => void;
  remove: (index: number) => void;
  move: (from: number, to: number) => void;
}> = ({ immutable, fields, append, remove, move }) => {
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const fieldToMove = fields.findIndex(
        (field) => getHighlightId(field) === active.id,
      );
      const fieldToInsert = fields.findIndex(
        (field) => getHighlightId(field) === over.id,
      );
      if (fieldToMove !== -1 && fieldToInsert !== -1) {
        move(fieldToMove, fieldToInsert);
      }
    }
  };

  return (
    <Card className="mb-6 pt-4" data-loc="manual-review-highlights-editor">
      {fields.length > 0 && (
        <ol className="mb-4 flex flex-col gap-y-4">
          <DndContext onDragEnd={handleDragEnd}>
            <SortableContext
              disabled={immutable}
              items={fields.map(getHighlightId)}
            >
              {fields.map((highlight, index) => {
                if (highlight.type === "file") return null;
                return (
                  <HighlightRow
                    key={getHighlightId(highlight)}
                    handleRemove={() => remove(index)}
                    highlight={highlight}
                    id={getHighlightId(highlight)}
                    immutable={immutable}
                    index={index}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </ol>
      )}
      <AddHighlightPopover
        append={(value) => append(value as HighlightsInner)}
        immutable={immutable}
      />
    </Card>
  );
};

// We do not have `id` property in highlights, but that property is set by react-hook-form
// And it is not stable, react-hook-form regenerates it when we reset data from server
// This function get stable id if it is available in highlight, or returns `id` as a fallback
// Separator has no stable id, but it's ok
const getHighlightId = (
  highlight: ManualReviewForm["highlights"][number] & { id: string },
) => {
  return highlight.type === "divider" ? highlight.id : highlight.id_value;
};
